import { NotificationTypePreference, UserDefinedNotification } from '@apis/Notification/model';
import { Drawer } from '@mantine/core';
import { useDiMemo } from '@root/Services/DI';
import { useEvent, useToggle } from '@root/Services/EventEmitter';
import { INotificationViewerOpenRequest, NotificationViewerService } from '@root/Services/Notification/NotificationViewerService';
import { BasicRouteLoader } from '@root/Services/Router/BasicRouteLoader';
import { useCallback, useEffect, useState } from 'react';
import { useRouteBoundPortal } from '../Router/RouteBoundPortal';
import { NotificationDefinitionSettingsLoader } from './Settings/NotificationSettingsLoader';
import { InAppNotificationViewer } from './Viewer/InAppNotificationViewer';

function InternalNotificationViewerOpener() {
    const viewerSvc = useDiMemo(NotificationViewerService);
    const [opened, { close, open }] = useToggle(false);
    const [request, setRequest] = useState<INotificationViewerOpenRequest>();
    const { mode, notificationId, typePrefId, recipientUserId } = request ?? {};
    const [lastRequest, setLastRequest] = useState<INotificationViewerOpenRequest>();
    const target = useRouteBoundPortal();

    useEffect(() => {
        const handleRequest = (request: undefined | INotificationViewerOpenRequest) => {
            if (request && !request.processed) {
                request.processed = true;
                setRequest(request);
                open();
            }
        };
        handleRequest(viewerSvc.openRequested.value);
        const { dispose } = viewerSvc.openRequested.listen(handleRequest);
        return dispose;
    }, []);

    const handleCopy = useCallback(
        (typePref: NotificationTypePreference) => {
            if (request) {
                close();
                const nextRequest: INotificationViewerOpenRequest = { ...request, typePrefId: typePref.Id, notificationId: undefined };
                setTimeout(() => viewerSvc.open(nextRequest), 300);
                viewerSvc.onSaved.emit(request?.typePrefId);
            }
        },
        [request]
    );

    const handleClose = useCallback(
        (changed?: boolean) => {
            request?.onClose?.(!!changed);
            if (changed) {
                viewerSvc.onSaved.emit(request?.typePrefId);
            }
            if (lastRequest) {
                setRequest(lastRequest);
                setLastRequest(undefined);
            } else {
                close();
            }
        },
        [request?.onClose, close, request, lastRequest]
    );

    const handleModeChange = useCallback((mode: 'view' | 'edit') => setRequest((r) => (!r ? undefined : { ...r, mode })), [setRequest]);
    const handleShowSettings = useCallback(
        (notification: UserDefinedNotification) => {
            setLastRequest(request);
            handleModeChange('edit');
            setRequest({ mode: 'edit', typePrefId: notification.NotificationTypePreferenceId ?? 0, scope: {} });
        },
        [handleModeChange, request]
    );

    const settingsSize = 500;
    const viewerSize = 800;
    const totalSize = (mode === 'edit' ? settingsSize : 0) + viewerSize;

    return (
        <Drawer
            opened={opened}
            size={totalSize}
            onClose={handleClose}
            target={target}
            withCloseButton={false}
            position="right"
            sx={{ flexWrap: 'nowrap' }}
        >
            {!opened ? null : mode === 'edit' ? (
                <NotificationDefinitionSettingsLoader
                    settingsWidth={settingsSize}
                    typePrefId={typePrefId}
                    onFinish={handleClose}
                    onCopy={handleCopy}
                    scope={request?.scope ?? {}}
                />
            ) : !notificationId ? null : (
                <InAppNotificationViewer
                    notificationId={notificationId}
                    recipientUserId={recipientUserId}
                    onClose={handleClose}
                    onShowSettings={handleShowSettings}
                />
            )}
        </Drawer>
    );
}

export function NotificationViewerProvider() {
    const routeLoader = useDiMemo(BasicRouteLoader);
    useEvent(routeLoader.routeMeta);

    return <InternalNotificationViewerOpener key={routeLoader.getTopRouteMeta().currentPathWithoutParams} />;
}

export function useNotificationViewer() {
    const { open, create } = useDiMemo(NotificationViewerService);
    return { open, create };
}
